import "./wdyr" // why did you render config
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import React from "react"
import ReactDOM from "react-dom/client"
import { QueryClient } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"

import Main from "./Main"
import reportWebVitals from "./reportWebVitals"
import i18n from "./i18n"
import { I18nextProvider } from "react-i18next"
import * as serviceWorkerRegistration from "./serviceWorkerRegistration"
import config from "config"

/**
 * 🔄 Note: `gcTime` controls how long a query stays *in memory* before garbage collection.
 * 🗃️ It does NOT affect how long data is persisted in storage.
 *
 * ⏳ Persistent storage (e.g., localStorage) retention is managed by `maxAge` in `persistOptions`.
 *
 * Example:
 * - `gcTime = 5 min`: query will be removed from memory if unused for 5 minutes.
 * - `maxAge = 10 min`: persisted data will be considered valid for 10 minutes, even after GC.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 15, // Keep unused queries in memory for 15 mins. ()
    },
  },
})

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
})

const root = ReactDOM.createRoot(document.getElementById("root"))
root.render(
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: Infinity,
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => {
          /**
           * 🔒 Define which queries should be persisted to storage
           *
           * 1. Persist specific query key(s), such as 'kiosk-config'
           * 2. Or persist queries with `meta.persist === true` explicitly set
           *
           * Example usage of meta:
           * useQuery({
           *   queryKey: ['user', userId],
           *   queryFn: fetchUser,
           *   meta: {
           *     persist: true,
           *   },
           * })
           */

          // 1. Persist specific queryKey(s)
          if (query.queryKey.includes("kiosk-config")) {
            return true
          }

          // 2. Persist based on custom meta flag
          if (query.meta?.persist) {
            return true
          }

          return false
        },
      },
    }}
  >
    <I18nextProvider i18n={i18n}>
      <Main />
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </I18nextProvider>
  </PersistQueryClientProvider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
if (config("offlineMode")) {
  serviceWorkerRegistration.register()
} else {
  serviceWorkerRegistration.unregister()
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
